import {
  DvGridOptionsService,
  DvRowCountStatusPanelComponent,
  DvRowCountStatusPanelParams,
} from '@devinco/ui/grid';
import { Injectable, inject } from '@angular/core';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import { TranslateService } from '@ngx-translate/core';
import {
  ColDef,
  ColGroupDef,
  GridApi,
  GridReadyEvent,
  IRowNode,
  SortModelItem,
  themeQuartz,
} from 'ag-grid-community';
import { deepCopy } from 'deep-copy-ts';
import { Subject, take } from 'rxjs';
import {
  FilterModelWithKeys,
  TreeDataConfig,
} from '../interfaces/grid-context';
import { GridOptions } from '../interfaces/grid-options';
import { GridDatasourceService } from '../services/grid-datasource.service';
import { getColumnTypes } from './column-types';
import { vtDefaultColDef } from './default-col-def';
import { sidebar } from './sidebar';
import { Entity } from '@shared/constants/entity';

const theme = themeQuartz.withParams({
  accentColor: 'var(--secondary500)',
  columnBorder: true,
  fontFamily: 'inherit',
  fontSize: 12,
  headerBackgroundColor: 'var(--grey50)',
  headerTextColor: 'var(--gray-70)',
  spacing: 4,
  oddRowBackgroundColor: 'var(--grey50)',
  selectedRowBackgroundColor: 'var(--secondary100)',
  wrapperBorderRadius: 4,
});

/** NOTE: this service is not singleton, so make sure that with every grid,
 *  new instance of the service would be created.
 * Not doing this causes issues for overlays when one grid in the same module is destroyed,
 * while another is created (loadingOverlay doesn't work for client side grids)
 */
@Injectable()
export class GridOptionsService {
  private vtGridDatasourceService = inject(GridDatasourceService);
  private restApiService = inject(RestApiService);
  private translate = inject(TranslateService);
  private dvGridOptionsService = inject(DvGridOptionsService);

  private colDefs: ColDef[];
  private entity: string;
  private gridApi?: GridApi;
  private rowData?: object[];

  clientSide(
    colDefs: ColDef[],
    entity: string,
    filterModel?: FilterModelWithKeys
  ): GridOptions {
    this.entity = entity;
    this.colDefs = colDefs;
    const gridDataLoaded$ = new Subject<number>();
    this.updateRowData(filterModel);
    return {
      ...this.vtCore(colDefs),
      context: {
        entity: entity,
        extraFilter: filterModel,
        loadClientSideData: (
          filterModel?: FilterModelWithKeys,
          selectFirstAfterUpdate = false
        ) => this.updateRowData(filterModel, undefined, selectFirstAfterUpdate),
        gridDataLoaded$,
      },
      onGridReady: ({ api }) => {
        this.gridApi = api;
        if (this.rowData) {
          // This means rowData was returned from backend before grid was ready!
          // Data was therefor cached in `this.rowData` until now `onGridReady`
          api.setGridOption('rowData', this.rowData);
        }
      },
      onRowDataUpdated: ({ api }) => {
        this.toggleNoRowsOverlay(api);
        gridDataLoaded$.next(api.getDisplayedRowCount());
      },
      onFilterChanged: ({ api }) => {
        this.toggleNoRowsOverlay(api);
        gridDataLoaded$.next(api.getDisplayedRowCount());
      },
    };
  }

  serverSide(
    colDefs: ColDef[],
    entity: string,
    extraFilter: FilterModelWithKeys = {},
    sortModel?: SortModelItem[],
    sum?: string[]
  ): GridOptions {
    const gridDataLoaded$ = new Subject<number>();
    const gridOptions: GridOptions = {
      ...this.vtCore(colDefs),
      context: {
        entity: entity,
        extraFilter: extraFilter,
        sum: sum,
        gridDataLoaded$,
      },
      rowModelType: 'serverSide',
      cacheBlockSize: 100,
      onGridReady: ({ api }: GridReadyEvent) => {
        api.setGridOption(
          'serverSideDatasource',
          this.vtGridDatasourceService.createServerSideDataSource(
            entity,
            gridDataLoaded$,
            sortModel
          )
        );
      },
      onModelUpdated({ api }) {
        !api.getDisplayedRowCount()
          ? api.showNoRowsOverlay()
          : api.hideOverlay();
      },
      rowSelection: {
        mode: 'singleRow',
        isRowSelectable: node => !node.group,
        checkboxes: false,
        enableClickSelection: true,
      },
      selectionColumnDef: undefined,
    };

    return gridOptions;
  }

  clientSideTreeData<T>(
    colDefs: (ColDef | ColGroupDef)[],
    entity: Entity,
    treeDataConfig: TreeDataConfig<T>
  ): GridOptions<T> {
    this.gridApi?.setGridOption('loading', true);
    this.entity = entity;
    // tree structure and row grouping and tree data can not be used simultaneously in ag-grid
    this.colDefs = colDefs
      .filter((colDef: ColDef) => !!colDef.field)
      .map(colDef => ({ ...colDef, enableRowGroup: false }));
    const gridDataLoaded$ = new Subject<number>();
    const gridOptions: GridOptions<T> = {
      ...this.vtCore(this.colDefs),
      context: {
        entity,
        gridDataLoaded$,
        treeDataConfig,
      },
      accentedSort: true,
      groupDefaultExpanded: -1,
      rowGroupPanelShow: 'never',
      treeData: true,
      autoGroupColumnDef: {
        cellRenderer: 'agGroupCellRenderer',
        field: treeDataConfig.groupDescriptionField as any,
        headerName: this.translate.instant(`${entity}.Group`),
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: (params: { value: string }) => params.value,
        },
        width: 300,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getDataPath: (data: any) => data.treePath,
      onGridReady: params => {
        this.gridApi = params.api;
        params.context.reloadTreeData =
          this.vtGridDatasourceService.setTreeRowData;
        params.context.reloadTreeData(params.api, params.context);
      },
    };
    return gridOptions;
  }

  /** Update client-side rowData */
  updateRowData = (
    filterModel?: FilterModelWithKeys,
    scrolledNodeAfterUpdate?: string,
    /** flag for client side grids to select first row after entity rowData update */
    selectFirstAfterUpdate = false
  ): void => {
    const colDefsWithField = this.colDefs.filter(c => c.field);
    const fields = colDefsWithField.map(c => c.field) as string[];
    let body: GetRowsRequest;
    if (filterModel !== undefined) {
      body = {
        entity: this.entity,
        fields: fields,
        filterModel,
      };
    } else {
      body = {
        entity: this.entity,
        fields: fields,
      };
    }

    if (!this.gridApi?.isDestroyed()) {
      this.gridApi?.setGridOption('loading', true);
    }

    this.restApiService
      .getData(body)
      .pipe(take(1))
      .subscribe(res => {
        const { data } = res;
        if (this.gridApi) {
          // setGridOption('rowData') resets selected row
          const nodeToSelectAfterLoadId = (
            this.gridApi.getSelectedNodes() as IRowNode[] | undefined
          )?.[0]?.id;

          this.gridApi.setGridOption('rowData', data);

          if (scrolledNodeAfterUpdate) {
            this.gridApi.clearFocusedCell();
            const nodeToScroll = this.gridApi.getRowNode(
              scrolledNodeAfterUpdate
            );
            this.gridApi.ensureNodeVisible(nodeToScroll);
          }

          if (selectFirstAfterUpdate) {
            this.gridApi.deselectAll();
            this.gridApi.getDisplayedRowAtIndex(0)?.setSelected(true, true);
          }

          if (nodeToSelectAfterLoadId) {
            this.gridApi.getRowNode(nodeToSelectAfterLoadId)?.setSelected(true);
          }
        } else {
          this.rowData = data;
        }

        this.gridApi?.setGridOption('loading', false);
      });
  };

  vtCore(colDefs: ColDef[]): GridOptions {
    const gridCoreOptions = deepCopy(this.dvGridOptionsService.core(colDefs));
    return {
      ...gridCoreOptions,
      theme: theme,
      context: {},
      defaultColDef: vtDefaultColDef,
      sideBar: deepCopy(sidebar),
      statusBar: {
        statusPanels: [
          {
            align: 'left',
            key: 'rowCountStatusPanel',
            statusPanel: DvRowCountStatusPanelComponent,
            statusPanelParams: {
              label: this.translate.instant('Grid.filteredRows'),
            } as DvRowCountStatusPanelParams,
          },
          { statusPanel: 'agAggregationComponent' },
        ],
      },
      noRowsOverlayComponentParams: {
        message: this.translate.instant('Grid.noRowsToShow'),
      },
      columnTypes: getColumnTypes(this.translate),
      selectionColumnDef: undefined,
    };
  }

  private toggleNoRowsOverlay(api: GridApi): void {
    !api.getDisplayedRowCount()
      ? api.setGridOption('activeOverlay', 'agNoRowsOverlay')
      : api.setGridOption('activeOverlay', false);
  }
}
